import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import { useCampaignDetail } from "../../../hooks/useCampaigns";
import {
  getContactTypeBadge,
  getCampaignName,
  hasDetailedStats,
  getCampaignStatusDisplay,
  stripHtml,
  getTimeAgo,
  extractButtonData,
} from "../../../constants/campaignConstants";
import {
  formatDateByLocale,
  formatNumberByLocale,
} from "../../../lib/localeFormatters";
import { useUiLocale } from "../../../hooks/useUiLocale";

const CampaignDetailPage = ({ initialCampaign = null }) => {
  const { t } = useTranslation("campaigns");
  const { locale } = useUiLocale();
  const { id } = useParams();
  const campaignId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();

  const campaignFromState = location.state?.campaign;
  const campaignData = initialCampaign || campaignFromState;

  const {
    data: campaign,
    isLoading,
    error,
    isError,
  } = useCampaignDetail(campaignId, campaignData);

  const handleBackToCampaigns = () => {
    navigate("/campaigns");
  };

  // Format date (converts UTC to local time, unless isLocalDate is true)
  const formatDate = (dateString, isLocalDate = false) => {
    if (!dateString) return t("detail.notScheduled");

    let dateToFormat;
    if (isLocalDate) {
      // For local dates (like end_at), interpret as-is without timezone conversion
      dateToFormat = dateString.replace(" ", "T");
    } else {
      // Ensure the date is interpreted as UTC by adding 'Z' if not present
      let utcDateString = dateString;
      if (!dateString.endsWith("Z") && !dateString.includes("+")) {
        utcDateString = dateString.replace(" ", "T") + "Z";
      }
      dateToFormat = utcDateString;
    }

    const date = new Date(dateToFormat);
    return formatDateByLocale(
      date,
      {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
      locale,
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-purple-500 sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-sm text-gray-400 sm:text-base">
            {t("detail.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !campaign)) {
    return (
      <div className="px-4 py-8 text-center sm:py-12">
        <Icon
          name="Megaphone"
          className="mx-auto mb-3 h-8 w-8 text-gray-400 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="mt-2 text-sm font-medium text-gray-300 sm:text-base">
          {t("detail.error.title")}
        </h3>
        <p className="mt-1 text-xs text-gray-400 sm:text-sm">
          {error?.message || t("detail.error.subtitle")}
        </p>
        <button
          onClick={handleBackToCampaigns}
          className="mt-3 rounded-lg bg-purple-600 px-3 py-2 text-sm text-white transition-colors hover:bg-purple-700 sm:mt-4 sm:px-4 sm:text-base"
        >
          {t("detail.actions.backToCampaigns")}
        </button>
      </div>
    );
  }

  const contactTypeBadge = getContactTypeBadge(campaign.contact_type);
  const showDetailedStats = hasDetailedStats(campaign.contact_type);

  const sentCount = campaign.sent_count || 0;
  const openCount = campaign.open_count || 0;
  const clickCount = campaign.clicked_count || 0;
  const openRate =
    sentCount > 0 ? ((openCount / sentCount) * 100).toFixed(1) : "0.0";
  const clickRate =
    sentCount > 0 ? ((clickCount / sentCount) * 100).toFixed(1) : "0.0";

  const statusDisplay = getCampaignStatusDisplay(campaign.status);

  const subject =
    campaign.contact_data?.title || campaign.contact_data?.subject || "";
  const body =
    campaign.contact_data?.body || campaign.contact_data?.message || "";
  const bodyText = stripHtml(body);
  const buttonData = extractButtonData(body);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleBackToCampaigns}
          className="rounded-lg p-2 transition-colors hover:bg-gray-800"
        >
          <Icon name="ArrowLeft" className="h-5 w-5 text-gray-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white sm:text-2xl">
              {getCampaignName(campaign)}
            </h1>
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.textColor}`}
            >
              {statusDisplay.label}
            </span>
          </div>
          <p className="mt-1 flex items-center gap-1 text-sm text-gray-400">
            <Icon name="Clock" className="h-3 w-3" />
            {t("detail.lastUpdated")} {getTimeAgo(campaign.updated_at)}
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Left Column - Stats and Info */}
        <div className="space-y-6 xl:col-span-2">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Total Sent */}
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
              <p className="mb-2 text-xs tracking-wider text-gray-400 uppercase">
                {t("stats.totalSent")}
              </p>
              <div className="flex items-baseline">
                <span className="text-2xl font-bold text-white">
                  {sentCount >= 1000
                    ? `${(sentCount / 1000).toFixed(
                        sentCount >= 10000 ? 0 : 1,
                      )}k`
                    : formatNumberByLocale(sentCount, {}, locale)}
                </span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-800">
                <div
                  className="h-full rounded-full bg-purple-500"
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>

            {/* Open Rate */}
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
              <p className="mb-2 text-xs tracking-wider text-gray-400 uppercase">
                {t("table.metrics.openRate")}
              </p>
              <div className="flex items-baseline">
                <span className="text-2xl font-bold text-white">
                  {showDetailedStats ? `${openRate}%` : "-"}
                </span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-800">
                <div
                  className="h-full rounded-full bg-cyan-500"
                  style={{ width: showDetailedStats ? `${openRate}%` : "0%" }}
                ></div>
              </div>
            </div>

            {/* Click Rate */}
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-5">
              <p className="mb-2 text-xs tracking-wider text-gray-400 uppercase">
                {t("table.metrics.ctr")}
              </p>
              <div className="flex items-baseline">
                <span className="text-2xl font-bold text-white">
                  {showDetailedStats ? `${clickRate}%` : "-"}
                </span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-800">
                <div
                  className="h-full rounded-full bg-green-500"
                  style={{
                    width: showDetailedStats
                      ? `${Math.min(parseFloat(clickRate) * 2, 100)}%`
                      : "0%",
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Campaign Configuration */}
          <div className="rounded-xl border border-gray-800 bg-gray-900">
            <div className="border-b border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" className="h-5 w-5 text-purple-400" />
                <h2 className="text-base font-semibold text-white">
                  {t("detail.configuration.title")}
                </h2>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Channel */}
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.channel")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon
                      name={contactTypeBadge.icon}
                      className={`h-4 w-4 ${contactTypeBadge.text}`}
                    />
                    <span className="text-white capitalize">
                      {campaign.contact_type || t("table.fallback.email")}
                    </span>
                  </div>
                </div>

                {/* Target Audience */}
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.targetAudience")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon name="Users" className="h-4 w-4 text-gray-400" />
                    <span className="text-white">
                      {campaign.audience_name ||
                        t("detail.configuration.allSubscribers")}
                    </span>
                  </div>
                </div>

                {/* Created At */}
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.createdAt")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" className="h-4 w-4 text-gray-400" />
                    <span className="text-white">
                      {formatDate(campaign.created_at)}
                    </span>
                  </div>
                </div>

                {/* End Date */}
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.configuration.endDate")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" className="h-4 w-4 text-gray-400" />
                    <span className="text-white">
                      {formatDate(campaign.end_at, true)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="rounded-xl border border-gray-800 bg-gray-900">
            <div className="border-b border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Icon name="Mail" className="h-5 w-5 text-purple-400" />
                <h2 className="text-base font-semibold text-white">
                  {t("detail.content.title")}
                </h2>
              </div>
            </div>
            <div className="space-y-4 p-4">
              {/* Subject Line */}
              {subject && (
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.content.subjectLine")}
                  </p>
                  <p className="text-white">{subject}</p>
                </div>
              )}

              {/* Body Text */}
              {bodyText && (
                <div>
                  <p className="mb-2 text-xs tracking-wider text-gray-500 uppercase">
                    {t("detail.content.bodyText")}
                  </p>
                  <div className="overflow-hidden rounded-lg bg-gray-800 p-4">
                    <p className="overflow-wrap-anywhere font-mono text-sm break-words whitespace-pre-wrap text-gray-300">
                      {bodyText}
                    </p>
                  </div>
                </div>
              )}

              {!subject && !bodyText && (
                <div className="py-6 text-center">
                  <Icon
                    name="FileText"
                    className="mx-auto mb-2 h-10 w-10 text-gray-600"
                  />
                  <p className="text-sm text-gray-400">
                    {t("detail.content.empty")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Device Preview */}
        <div className="xl:col-span-1">
          <div className="rounded-xl border border-gray-800 bg-gray-900 xl:sticky xl:top-6">
            <div className="border-b border-gray-800 p-4">
              <div className="flex items-center gap-2">
                <Icon name="Smartphone" className="h-5 w-5 text-gray-400" />
                <h2 className="text-base font-semibold text-white">
                  {t("detail.preview.title")}
                </h2>
              </div>
            </div>
            <div className="flex justify-center p-4">
              {/* Phone Frame */}
              <div className="w-full max-w-[280px]">
                <div className="rounded-[2.5rem] border-4 border-gray-800 bg-gray-950 p-2">
                  {/* Phone Notch */}
                  <div className="relative">
                    <div className="absolute top-0 left-1/2 z-10 flex h-6 w-20 -translate-x-1/2 transform items-center justify-center rounded-b-xl bg-gray-950">
                      <div className="h-2 w-2 rounded-full bg-gray-800"></div>
                      <div className="ml-1 h-2 w-2 rounded-full bg-gray-800"></div>
                    </div>

                    {/* Phone Screen */}
                    <div className="overflow-hidden rounded-[2rem] bg-white">
                      {/* Status Bar */}
                      <div className="flex items-center justify-between bg-gray-100 px-4 py-2 text-xs text-gray-600">
                        <span>9:41</span>
                        <div className="flex items-center gap-1">
                          <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                          <div className="h-2 w-4 rounded-sm bg-gray-400"></div>
                        </div>
                      </div>

                      {/* Email App Header */}
                      <div className="flex items-center justify-between bg-purple-600 px-4 py-3">
                        <Icon name="ArrowLeft" className="h-5 w-5 text-white" />
                        <span className="font-medium text-white">
                          {t("detail.preview.inbox")}
                        </span>
                        <Icon name="Search" className="h-5 w-5 text-white" />
                      </div>

                      {/* Email Content */}
                      <div className="min-h-[500px] p-4">
                        {/* Sender */}
                        <div className="mb-4 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                            <span className="text-sm font-bold text-purple-600">
                              F
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              Finitless
                            </p>
                            <p className="text-xs text-gray-500">
                              {t("detail.preview.toMe")}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {t("detail.preview.now")}
                          </span>
                        </div>

                        {/* Email Subject */}
                        <h3 className="mb-3 text-sm font-semibold text-gray-900">
                          {subject || t("detail.preview.defaultSubject")}
                        </h3>

                        {/* Email Body Preview */}
                        <div className="space-y-2 text-xs text-gray-600">
                          {bodyText ? (
                            <p className="whitespace-pre-wrap">
                              {bodyText.substring(0, 200)}
                              {bodyText.length > 200 ? "..." : ""}
                            </p>
                          ) : (
                            <>
                              <p>{t("detail.preview.fallbackBodyLine1")}</p>
                              <p>{t("detail.preview.fallbackBodyLine2")}</p>
                              <p>{t("detail.preview.fallbackBodyLine3")}</p>
                            </>
                          )}
                        </div>

                        {/* CTA Button */}
                        <div className="mt-6 border-t border-gray-100 pt-4 text-center">
                          {buttonData?.url ? (
                            <a
                              href={buttonData.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block max-w-[200px]"
                            >
                              <button
                                className="rounded-full px-8 py-3 text-xs font-bold text-white shadow-lg transition-opacity hover:opacity-90"
                                style={{ backgroundColor: buttonData.color }}
                              >
                                <span
                                  className="block overflow-hidden"
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {buttonData.text}
                                </span>
                              </button>
                            </a>
                          ) : (
                            <button
                              className="max-w-[200px] rounded-full px-8 py-3 text-xs font-bold text-white shadow-lg"
                              style={{
                                backgroundColor: buttonData?.color || "#06b6d4",
                              }}
                            >
                              <span
                                className="block overflow-hidden"
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.4",
                                }}
                              >
                                {buttonData?.text ||
                                  t("detail.preview.shopNow")}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetailPage;
